import { defineStore } from "pinia";
import { format } from "date-fns";
import { nanoid } from "nanoid";
import { TAXONOMIES, getPostTypeConfig } from "@facet/utils/constants";
import { useMediaQuery } from "@vueuse/core";

const LAYOUT_STORAGE_KEY = "facetLayout";

function getStoredLayout() {
    return localStorage.getItem(LAYOUT_STORAGE_KEY) || "grid";
}

function initTaxonomyState(postType) {
    const config = getPostTypeConfig(postType);

    if (!config) return {};

    return Object.fromEntries(
        config.taxonomies.map(t => [t.key, []])
    );
}

function parseConfig() {
    const appElement = document.querySelector("#facet");
    const configString = appElement?.dataset?.config;

    if (!configString) {
        return {
            post_type: 'event',
        };
    }

    try {
        return JSON.parse(configString);
    } catch (e) {
        console.error("Failed to parse data-config:", e);
        return {
            post_type: 'event',
        };
    }
}

export const useFeedStore = defineStore("facetStore", {
    state: () => {
        const config = parseConfig();

        return {
            // Config
            mode: "browse", // browse/search
            layout: getStoredLayout(), // grid/list
            postType: config.post_type,
            currentPage: 1,
            taxonomies: getPostTypeConfig(config.post_type).taxonomies,
            apiUrl: getPostTypeConfig(config.post_type).endpoint.posts,
            facetsUrl: getPostTypeConfig(config.post_type).endpoint.facets,

            // Data
            items: [],

            last_page: 0,
            total: 0,
            month: format(new Date(), "yyyy-MM"),
            filters: initTaxonomyState(config.post_type),
            facets: initTaxonomyState(config.post_type),
            search: "",

            // Conditionals
            isLoading: false,
            isToggled: false,
            isMobile: useMediaQuery("(max-width: 639px)")
        }
    },

    actions: {
        init() {

        },

        async fetchItems() {
            this.isLoading = true;

            const params = new URLSearchParams();

            if( this.postType === 'event' ){
                params.set("month", this.month);
            }else{
                if(this.search){
                    params.set("keywords", this.search);
                }
            }

            this.taxonomies.forEach(({ key }) => {
                const values = this.filters[key];
                if (Array.isArray(values) && values.length > 0) {
                    values.forEach((slug) => params.append(`${key}[]`, slug));
                }
            });

            if (this.currentPage !== 1) {
                params.set("page", this.currentPage);
            }

            this.items = [];
            try {
                const res = await fetch(`${this.apiUrl}?${params.toString()}`);
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

                const data = await res.json();

                this.items = data.data.map(item => ({
                    ...item,
                    __key: `${nanoid(10)}__${ item.ID }`
                })) || [];

                this.total = data.meta.total ?? 0;
                this.last_page = data.meta.last_page ?? 0;
            } catch (err) {
                console.error("Failed to fetch items:", err);
            } finally {
                this.isLoading = false;
            }
        },

        async fetchAllFacets() {
            try {
                const res = await fetch(this.facetsUrl);
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                const data = await res.json();
                console.log(data);
                this.facets = data;
            } catch (err) {
                console.error("Failed to fetch all facets:", err);
            }
        },

        async setMonthAndFetch(month) {
            this.month = month
            await this.fetchItems()
        },

        setMonth(month) {
            this.month = month;
        },

        setCurrentMonth() {
            const now = new Date();
            const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
            this.setMonth(month);
        },

        async clearFilters() {
            this.isLoading = true;
            Object.keys(this.filters).forEach((key) => {
                this.filters[key] = [];
            });
            this.setCurrentMonth();
            this.isToggled = false;
            this.search = "";
        },
    },
    getters: {

        showReset: (state) => {
            return state.hasFacetFilters || state.search
        },

        hasNoResults: (state) => {
            return !state.isLoading && state.total === 0;
        },

        hasFacetFilters: (state) => {
            return Object.values(state.filters).some(
                (arr) => Array.isArray(arr) && arr.length > 0
            )
        },

        hasMorePages: (state) => {
            return state.page < state.last_page;
        },

        showPagination: (state) => {
            return !state.isLoading && !state.hasNoResults && state.hasMorePages;
        },

        showMonthNavigation: (state) => {
            return state.postType === 'event';
        }
    },
});
